import Immutable from 'immutable';

import FileInfoBarcode from 'sharedreact/data/FileInfoBarcode';
import FileInfoColorSpace from 'sharedreact/data/FileInfoColorSpace';
import FileInfoDFDimension from 'sharedreact/data/FileInfoDFDimension';
import FileInfoExternalReference from 'sharedreact/data/FileInfoExternalReference';
import FileInfoInk from 'sharedreact/data/FileInfoInk';
import FileInfoLayer from 'sharedreact/data/FileInfoLayer';
import FileInfoProcessingStep from 'sharedreact/data/FileInfoProcessingStep';
import FileInfoFont from 'sharedreact/data/FileInfoFont';
import FileInfoScreenSet from 'sharedreact/data/FileInfoScreenSet';
import FileInfoScreenAndDgc from 'sharedreact/data/FileInfoScreenAndDgc';
import FileInfoStructuralDesign from 'sharedreact/data/FileInfoStructuralDesign';
import Url from 'sharedreact/data/Url';
import ClassFactory from 'sharedreact/utils/ClassFactory';
import { compareValues } from 'sharedreact/utils/CompareSupport';
import Encode from 'sharedreact/utils/Encode';
import FileSupport from 'sharedreact/utils/FileSupport';

import type moment from 'moment';

function splitOnLast (value: string, separator: string): string[] {
  const lastSeparatorIndex: number = value.lastIndexOf(separator);
  if (lastSeparatorIndex !== -1) {
    return [ value.substr(0, lastSeparatorIndex), value.substr(lastSeparatorIndex + 1) ];
  }
  return [value];
}

function last (stringList: string[], defaultValue: string): string {
  if (stringList.length > 1) {
    return stringList[stringList.length - 1];
  }
  return defaultValue;
}

const Content: {
  folder: string | null,
  filename: string | null,
  fileSize: number,
  fileType: string | null,
  imageSubType: string | null,
  creationDate: moment.Moment | null,
  modificationDate: moment.Moment | null,
  numberOfPages: number,
  dfVersion: number,
  creator: string | null,
  trimBoxHeight: number,
  trimBoxWidth: number,
  mediaBoxHeight: number,
  mediaBoxWidth: number,
  topMargin: number,
  bottomMargin: number,
  leftMargin: number,
  rightMargin: number,
  horResolution: number,
  verResolution: number,
  horScale: number,
  verScale: number,
  screenRegistration: boolean | null,
  colorSpace: FileInfoColorSpace | null,
  dfDimension: FileInfoDFDimension | null,
  inks: Immutable.List<FileInfoInk> | null,
  layers: Immutable.List<FileInfoLayer> | null,
  processingSteps: Immutable.List<FileInfoProcessingStep> | null,
  barcodes: Immutable.List<FileInfoBarcode> | null,
  fonts: Immutable.List<FileInfoFont> | null,
  externalReferences: Immutable.List<FileInfoExternalReference> | null,
  screenSets: Immutable.List<FileInfoScreenSet> | null,
  structuralDesigns: Immutable.List<FileInfoStructuralDesign> | null,
  screensAndDgc: Immutable.List<FileInfoScreenAndDgc> | null,
  cloudLink: string | null
} = {
  folder: null,
  filename: null,
  fileSize: -1,
  fileType: null,
  imageSubType: null,
  creationDate: null,
  modificationDate: null,
  numberOfPages: -1,
  dfVersion: -1,
  creator: null,
  trimBoxHeight: -1,
  trimBoxWidth: -1,
  mediaBoxHeight: -1,
  mediaBoxWidth: -1,
  topMargin: -1,
  bottomMargin: -1,
  leftMargin: -1,
  rightMargin: -1,
  horResolution: -1,
  verResolution: -1,
  horScale: -1,
  verScale: -1,
  screenRegistration: null,
  colorSpace: null,
  dfDimension: null,
  inks: null,
  layers: null,
  processingSteps: null,
  barcodes: null,
  fonts: null,
  externalReferences: null,
  screenSets: null,
  structuralDesigns: null,
  screensAndDgc: null,
  cloudLink: null
};

class FileInfo extends Immutable.Record(Content) {
  static createFromSide (side: any): FileInfo {
    return new FileInfo({
      folder: side.folder,
      filename: side.filename
    });
  }

  static createFromFile (file: string): FileInfo {
    const fileParts: string[] = splitOnLast(file, '/');
    const filename = decodeURIComponent(last(fileParts, ''));
    if (fileParts.length === 2) {
      return new FileInfo({
        folder: fileParts[0],
        filename: filename
      });
    } else {
      return new FileInfo({
        folder: '',
        filename: filename
      });
    }
  }

  static fromJS (json: object): FileInfo {
    return ClassFactory.createFromJson(json, FileInfo);
  }

  get fileTypeName (): string | undefined {
    return this.fileType != null ? FileSupport.fileTypeName(this.fileType) : undefined;
  }

  get fileUrl (): string | null {
    if (this.folder && this.filename) {
      const fileUrl = this.folder + '/' + this.filename.split('/').map((component) => FileSupport.strictEncodeURIComponent(component)).join('/');
      return Encode.normalizeHexChars(fileUrl);
    }
    return null;
  }

  toUrl (): Url | null {
    if (this.folder && this.filename) {
      return new Url({folder: this.folder, filename: this.filename});
    }
    return null;
  }

  isDigitalFilm (): boolean {
    return this.dfVersion !== -1;
  }

  isImage (): boolean {
    return (
      compareValues(this.imageSubType, 'CT') === 0 ||
      compareValues(this.imageSubType, 'LP') === 0 ||
      compareValues(this.imageSubType, 'HC') === 0
    );
  }

  isPdf (): boolean | undefined {
    return this.fileType != null ? this.fileType.indexOf('PDF') !== -1 : undefined;
  }

  getCloudViewerURL(): string | null {
    if (this.cloudLink != null && this.cloudLink.length > 0 && this.cloudLink.startsWith('eskocloud://')) {
      // xmp manageTo is in format eskocloud://next.dev.cloudi.city/rU8zhUFM5hSx-CNiWGTwJu2R
      const parts: string[] = this.cloudLink.split('/');
      if (parts.length >= 4 && parts[2].length > 0 && parts[3].length > 0) {
       //format: approve.next.dev.cloudi.city/viewer/rU8zhUFM5hSx-CNiWGTwJu2R
        return 'https://approve.' + parts[2] + '/viewer/' + parts[3];
      }
    }
    return null;
  }
}

export default FileInfo;
